import React, { useState, useEffect, useRef } from 'react';
import './AdminProductsPage.css';
import ProductImagesModal from '../components/ProductImagesModal';
import { ADMIN_API_URL } from '../services/api.config';

export default function AdminProductsPage({ userData }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  // Image upload related states
  const [uploadStatus, setUploadStatus] = useState({});
  const fileInputRefs = useRef({});
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // New state for product images modal
  const [showProductImagesModal, setShowProductImagesModal] = useState(false);
  const [selectedProductForImages, setSelectedProductForImages] = useState(null);
  
  // Initial empty product form structure
  const emptyProductForm = {
    ProductId: '',
    title: { en: '', ua: '' },
    description: { en: '', ua: '' },
    tagline: { en: '', ua: '' },
    category: '',
    collection: '',
    params: [],
    prices: []
  };
  
  const [productFormData, setProductFormData] = useState(emptyProductForm);

  // Функція для перевірки, чи ціни мають правильну структуру
  const arePricesValid = () => {
    if (!Array.isArray(productFormData.prices) || productFormData.prices.length === 0) {
      return false;
    }
    
    // Перевіряємо, чи є параметри типу "price"
    const priceParams = productFormData.params.filter(param => param.type === 'price');
    if (priceParams.length === 0) {
      return false;
    }
    
    // Перевіряємо, чи кожен елемент цін має необхідні поля для параметрів
    // Не перевіряємо наявність поля price, бо воно може бути відсутнім у старих даних
    for (const priceItem of productFormData.prices) {
      // Перевіряємо, чи є всі параметри ціни
      for (const param of priceParams) {
        if (!(param.code in priceItem)) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Функція для перетворення тексту у camelCase
  const toCamelCase = (text) => {
    if (!text) return '';
    
    // Очищаємо текст від спеціальних символів
    const cleanText = text
      .trim()
      .replace(/[^\w\s]/g, '') // Видаляємо всі символи, крім букв, цифр та пробілів
      .replace(/\s+/g, ' ');   // Замінюємо кілька пробілів на один
    
    // Перетворюємо у camelCase
    return cleanText
      .toLowerCase()
      .split(/\s+/)
      .map((word, i) => {
        // Перше слово починається з малої літери, наступні - з великої
        return i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('');
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);
  
  // Додаємо ефект для автоматичного оновлення цін при зміні параметрів
  useEffect(() => {
    // Якщо немає параметрів або це перше завантаження, не оновлюємо ціни
    if (productFormData.params.length === 0 || !showProductModal) {
      return;
    }
    
    // Перевіряємо, чи змінились параметри типу "price"
    const priceParams = productFormData.params.filter(param => param.type === 'price');
    
    // Якщо немає параметрів типу "price", не оновлюємо ціни
    if (priceParams.length === 0) {
      return;
    }
    
    // Не оновлюємо ціни автоматично, користувач має натиснути кнопку "Update Prices"
  }, [productFormData.params]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${ADMIN_API_URL}/admin/products`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      setProducts(data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${ADMIN_API_URL}/admin/categories?showHidden=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      // Don't set the main error state to avoid blocking the UI
    }
  };

  const openCreateProductModal = () => {
    setEditMode(false);
    setProductFormData(emptyProductForm);
    setFormError('');
    setFormSuccess('');
    setShowProductModal(true);
  };

  const openEditProductModal = (product) => {
    setEditMode(true);
    setSelectedProduct(product);
    
    // Переконаємось, що prices є масивом
    let productPrices = Array.isArray(product.prices) ? product.prices : [];
    
    // Нормалізуємо структуру цін, додаючи поле price якщо воно відсутнє
    productPrices = productPrices.map(priceItem => {
      if (!priceItem.price) {
        return {
          ...priceItem,
          price: { en: '', ua: '' }
        };
      }
      return priceItem;
    });

    setProductFormData({
      ProductId: product.ProductId || '',
      title: product.title || { en: '', ua: '' },
      description: product.description || { en: '', ua: '' },
      tagline: product.tagline || { en: '', ua: '' },
      category: product.category || '',
      collection: product.collection || '',
      params: Array.isArray(product.params) ? product.params : [],
      prices: productPrices
    });

    setFormError('');
    setFormSuccess('');
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
    setFormError('');
    setFormSuccess('');
  };

  const handleFormChange = (e, field, lang) => {
    if (lang) {
      setProductFormData({
        ...productFormData,
        [field]: {
          ...productFormData[field],
          [lang]: e.target.value
        }
      });
    } else {
      setProductFormData({
        ...productFormData,
        [field]: e.target.value
      });
    }
  };

  // Parameter handling functions
  const handleParamChange = (index, field, lang, value) => {
    const updatedParams = [...productFormData.params];
    
    // If the param doesn't exist at this index, create it
    if (!updatedParams[index]) {
      updatedParams[index] = { 
        code: `param-${Date.now().toString(36)}-${index}`,
        name: { en: '', ua: '' }, 
        type: 'price', 
        displayType: 'block',
        values: []
      };
    }
    
    // Update the field based on the type of change
    if (field === 'name' && lang) {
      updatedParams[index] = {
        ...updatedParams[index],
        name: {
          ...updatedParams[index].name,
          [lang]: value
        }
      };
    } else {
      updatedParams[index] = {
        ...updatedParams[index],
        [field]: value
      };
    }
    
    setProductFormData({
      ...productFormData,
      params: updatedParams
    });
  };

  // Generate all possible price combinations based on price parameters
  const generatePriceCombinations = () => {
    // Get all parameters of type "price"
    const priceParams = productFormData.params.filter(param => param.type === 'price');
    
    // If no price parameters, return empty array
    if (priceParams.length === 0) {
      return [];
    }
    
    // Helper function to generate all combinations recursively
    const generateCombinations = (params, currentIndex, currentCombination) => {
      // Base case: we've processed all parameters
      if (currentIndex === params.length) {
        return [currentCombination];
      }
      
      const currentParam = params[currentIndex];
      const combinations = [];
      
      // If parameter has no values, skip it
      if (!currentParam.values || currentParam.values.length === 0) {
        return generateCombinations(params, currentIndex + 1, currentCombination);
      }
      
      // For each value of the current parameter
      for (const value of currentParam.values) {
        // Create a new combination with this value
        const newCombination = {
          ...currentCombination,
          [currentParam.code]: value.code
        };
        
        // Recursively generate combinations for the next parameters
        const nextCombinations = generateCombinations(params, currentIndex + 1, newCombination);
        combinations.push(...nextCombinations);
      }
      
      return combinations;
    };
    
    // Start the recursive generation with an empty combination
    const allCombinations = generateCombinations(priceParams, 0, {});
    
    // Add price object to each combination
    return allCombinations.map(combination => ({
      ...combination,
      price: { en: '', ua: '' }
    }));
  };
  
  // Update prices based on parameters
  const updatePrices = () => {
    // First, generate codes for all parameters and their values
    const updatedParams = [...productFormData.params].map(param => {
      // Always generate code for parameter based on English name
      let updatedParam = { ...param };
      if (updatedParam.name && updatedParam.name.en) {
        updatedParam.code = toCamelCase(updatedParam.name.en);
      }
      
      // Always generate codes for all values based on their English names
      if (updatedParam.values && updatedParam.values.length > 0) {
        updatedParam.values = updatedParam.values.map(value => {
          let updatedValue = { ...value };
          if (updatedValue.name && updatedValue.name.en) {
            updatedValue.code = toCamelCase(updatedValue.name.en);
          }
          return updatedValue;
        });
      }
      
      return updatedParam;
    });
    
    // Update the form data with the updated parameters
    setProductFormData({
      ...productFormData,
      params: updatedParams
    });
    
    // Get all parameters of type "price" from the updated params
    const priceParams = updatedParams.filter(param => param.type === 'price');
    
    // If no price parameters, show an alert
    if (priceParams.length === 0) {
      alert("Please add at least one parameter with type 'price' before updating prices.");
      return;
    }
    
    // Generate all possible price combinations
    const newPriceCombinations = generatePriceCombinations();
    
    // If there are existing prices, try to preserve their values
    if (productFormData.prices && productFormData.prices.length > 0) {
      // For each new combination, check if there's a matching existing price
      for (const newCombination of newPriceCombinations) {
        const existingPrice = productFormData.prices.find(price => {
          // Check if all parameter values match
          const priceParams = updatedParams.filter(param => param.type === 'price');
          return priceParams.every(param => price[param.code] === newCombination[param.code]);
        });
        
        // If found, copy the price values
        if (existingPrice && existingPrice.price) {
          newCombination.price = { ...existingPrice.price };
        }
      }
    }
    
    // Update the form data with the new price combinations
    setProductFormData(prevState => ({
      ...prevState,
      prices: newPriceCombinations
    }));
  };

  // Handle value changes for parameter options
  const handleValueChange = (paramIndex, valueIndex, field, lang, value) => {
    const updatedParams = [...productFormData.params];
    const param = updatedParams[paramIndex];
    
    // Ensure values array exists
    if (!param.values) {
      param.values = [];
    }
    
    // Ensure the value object exists
    if (!param.values[valueIndex]) {
      param.values[valueIndex] = {
        code: `value-${Date.now().toString(36)}-${valueIndex}`,
        name: { en: '', ua: '' }
      };
    }
    
    // Update the field based on the type of change
    if (field === 'name' && lang) {
      param.values[valueIndex] = {
        ...param.values[valueIndex],
        name: {
          ...param.values[valueIndex].name,
          [lang]: value
        }
      };
    } else {
      param.values[valueIndex] = {
        ...param.values[valueIndex],
        [field]: value
      };
    }
    
    setProductFormData({
      ...productFormData,
      params: updatedParams
    });
  };
  
  // Add a new value to a parameter
  const addValue = (paramIndex) => {
    const updatedParams = [...productFormData.params];
    const param = updatedParams[paramIndex];
    
    // Ensure values array exists
    if (!param.values) {
      param.values = [];
    }
    
    const newValue = {
      code: `value-${Date.now().toString(36)}-${param.values.length}`,
      name: { en: '', ua: '' }
    };
    
    param.values.push(newValue);
    
    setProductFormData({
      ...productFormData,
      params: updatedParams
    });
  };
  
  // Remove a value from a parameter
  const removeValue = (paramIndex, valueIndex) => {
    const updatedParams = [...productFormData.params];
    const param = updatedParams[paramIndex];
    
    if (param.values && param.values.length > valueIndex) {
      param.values.splice(valueIndex, 1);
      
      setProductFormData({
        ...productFormData,
        params: updatedParams
      });
    }
  };

  const addParam = () => {
    const newParamIndex = productFormData.params.length;
    const newParam = {
      code: `param-${Date.now().toString(36)}-${newParamIndex}`,
      name: { en: '', ua: '' },
      type: 'price',
      displayType: 'block',
      values: []
    };
    
    setProductFormData({
      ...productFormData,
      params: [...productFormData.params, newParam]
    });
  };

  const removeParam = (index) => {
    const updatedParams = [...productFormData.params];
    updatedParams.splice(index, 1);
    setProductFormData({
      ...productFormData,
      params: updatedParams
    });
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`${ADMIN_API_URL}/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err.message);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setFormSubmitting(true);

    // Basic validation
    if (!editMode && !productFormData.ProductId.trim()) {
      setFormError('Product ID is required');
      setFormSubmitting(false);
      return;
    }

    if (!productFormData.title.en.trim() || !productFormData.title.ua.trim()) {
      setFormError('Title is required in both languages');
      setFormSubmitting(false);
      return;
    }

    if (!productFormData.category.trim()) {
      setFormError('Category is required');
      setFormSubmitting(false);
      return;
    }

    // Validate parameters
    if (productFormData.params && productFormData.params.length > 0) {
      // Check for parameters with empty names
      const emptyParamNames = productFormData.params.some(
        param => !param.name?.en?.trim() || !param.name?.ua?.trim()
      );
      
      if (emptyParamNames) {
        setFormError('All parameters must have names in both English and Ukrainian');
        setFormSubmitting(false);
        return;
      }
      
      // Check for parameters with empty codes
      const emptyParamCodes = productFormData.params.some(param => !param.code?.trim());
      if (emptyParamCodes) {
        setFormError('All parameters must have codes. Please click "Update Prices" to generate codes.');
        setFormSubmitting(false);
        return;
      }
      
      // Check for parameters with empty values
      const emptyValueParams = productFormData.params.filter(
        param => !param.values || param.values.length === 0
      );
      
      if (emptyValueParams.length > 0) {
        const paramNames = emptyValueParams.map(p => p.name?.en || 'Unnamed parameter').join(', ');
        setFormError(`Parameters must have at least one value: ${paramNames}`);
        setFormSubmitting(false);
        return;
      }
      
      // Check for values with empty names or codes
      for (const param of productFormData.params) {
        if (param.values && param.values.length > 0) {
          const emptyValueNames = param.values.some(
            value => !value.name?.en?.trim() || !value.name?.ua?.trim()
          );
          
          if (emptyValueNames) {
            setFormError(`Parameter "${param.name?.en || 'Unnamed'}" has values with empty names`);
            setFormSubmitting(false);
            return;
          }
          
          const emptyValueCodes = param.values.some(value => !value.code?.trim());
          if (emptyValueCodes) {
            setFormError(`Parameter "${param.name?.en || 'Unnamed'}" has values with empty codes. Please click "Update Prices" to generate codes.`);
            setFormSubmitting(false);
            return;
          }
        }
      }
    }
    
    // Validate prices if there are price parameters
    const priceParams = productFormData.params.filter(param => param.type === 'price');
    if (priceParams.length > 0) {
      // Check if prices exist
      if (!productFormData.prices || productFormData.prices.length === 0) {
        setFormError('Product has price parameters but no prices defined. Please click "Update Prices" to generate price combinations.');
        setFormSubmitting(false);
        return;
      }
      
      // Check for empty price values
      const emptyPrices = productFormData.prices.some(
        price => !price.price?.en?.toString().trim() || !price.price?.ua?.toString().trim()
      );
      
      if (emptyPrices) {
        setFormError('All price combinations must have values in both English and Ukrainian');
        setFormSubmitting(false);
        return;
      }
    }

    try {
      const method = editMode ? 'PUT' : 'POST';
      const url = editMode 
        ? `${ADMIN_API_URL}/admin/products/${productFormData.ProductId}`
        : `${ADMIN_API_URL}/admin/products`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        },
        body: JSON.stringify(productFormData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save product');
      }

      setFormSuccess(editMode ? 'Product updated successfully' : 'Product created successfully');
      fetchProducts();
      
      // Close modal after short delay to show success message
      setTimeout(() => {
        closeProductModal();
      }, 1500);
    } catch (err) {
      console.error('Error saving product:', err);
      setFormError(err.message || 'An error occurred while saving the product');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Image upload related functions
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };
  
  const triggerFileInput = (productId) => {
    if (fileInputRefs.current[productId]) {
      fileInputRefs.current[productId].click();
    }
  };
  
  const handleFileInputChange = async (productId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Перевірка, чи файл має допустимий формат
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        [productId]: { success: false, message: 'Only PNG, JPEG, JPG and WEBP files are allowed' }
      });
      return;
    }

    try {
      setUploadStatus({
        ...uploadStatus,
        [productId]: { loading: true }
      });

      // Конвертація файлу в base64
      const base64Image = await convertFileToBase64(file);
      const base64String = base64Image.split(',')[1]; // Видаляємо префікс "data:image/png;base64,"

      // Відправка на сервер
      const response = await fetch(`${ADMIN_API_URL}/admin/products/${productId}/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userData.token}`
        },
        body: JSON.stringify({
          image: base64String
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload image');
      }

      setUploadStatus({
        ...uploadStatus,
        [productId]: { success: true, message: 'Image uploaded successfully' }
      });

      // Оновлюємо список продуктів
      fetchProducts();

      // Очищаємо input
      if (fileInputRefs.current[productId]) {
        fileInputRefs.current[productId].value = '';
      }

    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadStatus({
        ...uploadStatus,
        [productId]: { success: false, message: err.message }
      });
    }
  };
  
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };
  
  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };
  
  const openProductImagesModal = (productId) => {
    setSelectedProductForImages(productId);
    setShowProductImagesModal(true);
  };
  
  const closeProductImagesModal = () => {
    setShowProductImagesModal(false);
    setSelectedProductForImages(null);
  };

  return (
    <div className="admin-products-page">
      <div className="admin-products-header">
        <h1>Products Management</h1>
        <div className="admin-products-actions">
          <button className="btn-add-product" onClick={openCreateProductModal}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add New Product
          </button>
        </div>
      </div>

      {loading ? (
        <div className="admin-products-loading">
          <div className="loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      ) : error ? (
        <div className="admin-products-error">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <p>Error: {error}</p>
        </div>
      ) : products.length === 0 ? (
        <div className="no-products">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="20" height="16" rx="2"></rect>
            <path d="M22 8a2 2 0 00-2-2h-8L8 3a1 1 0 00-1-1H2"></path>
          </svg>
          <p>No products found</p>
          <button className="btn-add-first-product" onClick={openCreateProductModal}>
            Add Your First Product
          </button>
        </div>
      ) : (
        <div className="admin-products-grid">
          {products.map(product => (
            <div key={product.ProductId} className="product-card">
              <div className="product-header">
                <h3>{product.title?.en || 'Unnamed Product'}</h3>
                <div className="product-actions">
                  <button 
                    className="btn-edit"
                    onClick={() => openEditProductModal(product)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                    </svg>
                    Edit
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeleteProduct(product.ProductId)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
              <div className="product-content">
                <div className="product-image-container">
                  {product.mainImage ? (
                    <div className="product-image-wrapper">
                      <img 
                        src={product.mainImage}
                        alt={product.title?.en || 'Product image'}
                        className="product-main-image"
                        onClick={() => handleImageClick(product.mainImage)}
                      />
                    </div>
                  ) : (
                    <div className="product-image-placeholder">
                      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <p>No image uploaded</p>
                    </div>
                  )}
                  
                  <div className="image-actions">
                    <button 
                      className="btn-upload-image"
                      onClick={() => triggerFileInput(product.ProductId)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      {product.mainImage ? 'Change Image' : 'Upload Image'}
                    </button>
                    
                    <button 
                      className="btn-all-images"
                      onClick={() => openProductImagesModal(product.ProductId)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                      All Images
                    </button>
                    
                    <input 
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="file-input-hidden"
                      ref={el => fileInputRefs.current[product.ProductId] = el}
                      onChange={(e) => handleFileInputChange(product.ProductId, e)}
                    />
                  </div>
                  
                  {uploadStatus[product.ProductId] && (
                    <div className={`upload-status ${uploadStatus[product.ProductId].success ? 'success' : 'error'}`}>
                      {uploadStatus[product.ProductId].loading ? (
                        <div className="upload-loading">
                          <div className="upload-spinner"></div>
                          Uploading...
                        </div>
                      ) : (
                        uploadStatus[product.ProductId].message
                      )}
                    </div>
                  )}
                </div>
                <div className="product-details">
                  <div className="product-id">{product.ProductId}</div>
                  <div className="product-meta">
                    <div className="meta-item">
                      <strong>Category:</strong> {product.category}
                    </div>
                    {product.collection && (
                      <div className="meta-item">
                        <strong>Collection:</strong> {product.collection}
                      </div>
                    )}
                  </div>
                  <div className="product-translations">
                    <div className="translation-section">
                      <h4>English</h4>
                      <div className="translation-item">
                        <span className="lang-badge">EN</span>
                        <span>{product.title?.en || '-'}</span>
                      </div>
                      {product.tagline?.en && (
                        <div className="translation-item">
                          <span className="lang-badge">EN</span>
                          <span className="tagline">{product.tagline.en}</span>
                        </div>
                      )}
                    </div>
                    <div className="translation-section">
                      <h4>Ukrainian</h4>
                      <div className="translation-item">
                        <span className="lang-badge">UA</span>
                        <span>{product.title?.ua || '-'}</span>
                      </div>
                      {product.tagline?.ua && (
                        <div className="translation-item">
                          <span className="lang-badge">UA</span>
                          <span className="tagline">{product.tagline.ua}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showProductModal && (
        <div className="product-modal">
          <div className="modal-content product-form-modal">
            <div className="modal-header">
              <h2>{editMode ? 'Edit Product' : 'Create New Product'}</h2>
              <button className="close-modal" onClick={closeProductModal}>×</button>
            </div>
            <form onSubmit={handleProductSubmit} className="product-form">
              
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-group">
                  <label>Product ID</label>
                  <input 
                    type="text" 
                    value={productFormData.ProductId} 
                    onChange={(e) => handleFormChange(e, 'ProductId')}
                    disabled={editMode}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={productFormData.category}
                    onChange={(e) => handleFormChange(e, 'category')}
                    required
                    className="category-select"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option 
                        key={category.CategoryId} 
                        value={category.CategoryId}
                        className={category.dontShow ? 'hidden-category' : ''}
                      >
                        {category.title?.en || category.CategoryId}
                        {category.dontShow ? ' (Hidden)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Collection (optional)</label>
                  <input 
                    type="text" 
                    value={productFormData.collection} 
                    onChange={(e) => handleFormChange(e, 'collection')}
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h3>Title</h3>
                <div className="form-group">
                  <label>English Title</label>
                  <input 
                    type="text" 
                    value={productFormData.title.en} 
                    onChange={(e) => handleFormChange(e, 'title', 'en')}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Ukrainian Title</label>
                  <input 
                    type="text" 
                    value={productFormData.title.ua} 
                    onChange={(e) => handleFormChange(e, 'title', 'ua')}
                    required
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h3>Tagline (optional)</h3>
                <div className="form-group">
                  <label>English Tagline</label>
                  <input 
                    type="text" 
                    value={productFormData.tagline.en} 
                    onChange={(e) => handleFormChange(e, 'tagline', 'en')}
                  />
                </div>
                
                <div className="form-group">
                  <label>Ukrainian Tagline</label>
                  <input 
                    type="text" 
                    value={productFormData.tagline.ua} 
                    onChange={(e) => handleFormChange(e, 'tagline', 'ua')}
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h3>Description</h3>
                <div className="form-group">
                  <label>English Description</label>
                  <textarea 
                    value={productFormData.description.en} 
                    onChange={(e) => handleFormChange(e, 'description', 'en')}
                    rows={4}
                  />
                </div>
                
                <div className="form-group">
                  <label>Ukrainian Description</label>
                  <textarea 
                    value={productFormData.description.ua} 
                    onChange={(e) => handleFormChange(e, 'description', 'ua')}
                    rows={4}
                  />
                </div>
              </div>
              
              <div className="form-section">
                <h3>Parameters</h3>
                {productFormData.params.map((param, index) => (
                  <div key={param.code || index} className="parameter-item">
                    <div className="param-row">
                      <div className="param-name-column">
                        <div className="param-name-input">
                          <label>English Name</label>
                          <input 
                            type="text" 
                            value={param.name?.en || ''} 
                            onChange={(e) => handleParamChange(index, 'name', 'en', e.target.value)}
                            placeholder="Parameter name (English)"
                          />
                        </div>
                        <div className="param-name-input">
                          <label>Ukrainian Name</label>
                          <input 
                            type="text" 
                            value={param.name?.ua || ''} 
                            onChange={(e) => handleParamChange(index, 'name', 'ua', e.target.value)}
                            placeholder="Parameter name (Ukrainian)"
                          />
                        </div>
                        <div className="param-code">
                          <label>Code</label>
                          <div className="code-display">
                            {param.code || 'Generated code will appear here'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="param-type-column">
                        <label>Type</label>
                        <select 
                          value={param.type || 'price'} 
                          onChange={(e) => handleParamChange(index, 'type', null, e.target.value)}
                        >
                          <option value="price">Price</option>
                          <option value="link">Link</option>
                        </select>
                      </div>
                      
                      <div className="param-display-column">
                        <label>Display</label>
                        <select 
                          value={param.displayType || 'block'} 
                          onChange={(e) => handleParamChange(index, 'displayType', null, e.target.value)}
                        >
                          <option value="block">Block</option>
                          <option value="color">Color</option>
                        </select>
                      </div>
                      
                      <div className="param-actions">
                        <button 
                          type="button"
                          className="btn-remove-param" 
                          onClick={() => removeParam(index)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="values-section">
                      <h4>Options</h4>
                      {param.values && param.values.length > 0 ? (
                        <table className="values-table">
                          <thead>
                            <tr>
                              <th>English Name</th>
                              <th>Ukrainian Name</th>
                              <th>Code</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {param.values.map((value, valueIndex) => (
                              <tr key={value.code || valueIndex}>
                                <td>
                                  <input 
                                    type="text" 
                                    value={value.name?.en || ''} 
                                    onChange={(e) => handleValueChange(index, valueIndex, 'name', 'en', e.target.value)}
                                    placeholder="Value name (English)"
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="text" 
                                    value={value.name?.ua || ''} 
                                    onChange={(e) => handleValueChange(index, valueIndex, 'name', 'ua', e.target.value)}
                                    placeholder="Value name (Ukrainian)"
                                  />
                                </td>
                                <td className="code-cell">
                                  <div className="code-display value-code">
                                    {value.code || ''}
                                  </div>
                                </td>
                                <td>
                                  <button 
                                    type="button" 
                                    className="btn-remove-value" 
                                    onClick={() => removeValue(index, valueIndex)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="no-values-message">No options added yet</div>
                      )}
                      <button 
                        type="button" 
                        className="btn-add-value" 
                        onClick={() => addValue(index)}
                      >
                        Add Option
                      </button>
                    </div>
                  </div>
                ))}
                <div className="buttons-row">
                  <button 
                    type="button" 
                    className="btn-add" 
                    onClick={addParam}
                  >
                    Add Parameter
                  </button>
                  <button 
                    type="button" 
                    className="btn-update-prices" 
                    onClick={updatePrices}
                  >
                    Update Prices
                  </button>
                </div>
              </div>
              
              <div className="form-section">
                <h3 className="prices-heading">Prices</h3>
                <div className="prices-container">
                  
                  {arePricesValid() ? (
                    <table className="prices-table">
                      <thead>
                        <tr>
                          {/* Generate headers for each price parameter */}
                          {productFormData.params
                            .filter(param => param.type === 'price')
                            .map(param => (
                              <th key={param.code}>{param.code}</th>
                            ))
                          }
                          <th>English Price</th>
                          <th>Ukrainian Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productFormData.prices.map((priceItem, index) => (
                          <tr key={index}>
                            {/* Generate cells for each price parameter */}
                            {productFormData.params
                              .filter(param => param.type === 'price')
                              .map(param => {
                                // Find the parameter value that matches this price item
                                const paramValue = param.values.find(v => v.code === priceItem[param.code]);
                                return (
                                  <td key={param.code}>
                                    {paramValue ? paramValue.code : priceItem[param.code]}
                                  </td>
                                );
                              })
                            }
                            <td>
                              <input
                                type="number"
                                value={priceItem.price?.en || ''}
                                onChange={(e) => {
                                  const updatedPrices = [...productFormData.prices];
                                  if (!updatedPrices[index].price) {
                                    updatedPrices[index].price = { en: '', ua: '' };
                                  }
                                  updatedPrices[index].price.en = e.target.value;
                                  setProductFormData({
                                    ...productFormData,
                                    prices: updatedPrices
                                  });
                                }}
                                placeholder="Price (English)"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={priceItem.price?.ua || ''}
                                onChange={(e) => {
                                  const updatedPrices = [...productFormData.prices];
                                  if (!updatedPrices[index].price) {
                                    updatedPrices[index].price = { en: '', ua: '' };
                                  }
                                  updatedPrices[index].price.ua = e.target.value;
                                  setProductFormData({
                                    ...productFormData,
                                    prices: updatedPrices
                                  });
                                }}
                                placeholder="Price (Ukrainian)"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="no-prices-message">No prices defined. Use the "Update Prices" button to generate price combinations.</div>
                  )}
                </div>
              </div>
              
              {formError && <div className="form-error validation-bottom">{formError}</div>}
              {formSuccess && <div className="form-success validation-bottom">{formSuccess}</div>}
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel" 
                  onClick={closeProductModal}
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-save" 
                  disabled={formSubmitting}
                >
                  {formSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Saving...
                    </>
                  ) : (
                    editMode ? 'Update Product' : 'Create Product'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showImageModal && selectedImage && (
        <div className="image-modal">
          <div className="modal-content image-modal-content">
            <div className="modal-header">
              <h2>Product Image</h2>
              <button className="close-modal" onClick={closeImageModal}>×</button>
            </div>
            <div className="modal-body image-modal-body">
              <img src={selectedImage} alt="Product" className="modal-image" />
            </div>
          </div>
        </div>
      )}
      
      {/* Product Images Modal */}
      {showProductImagesModal && selectedProductForImages && (
        <ProductImagesModal 
          productId={selectedProductForImages}
          userData={userData}
          onClose={closeProductImagesModal}
          onUpdate={fetchProducts}
        />
      )}
    </div>
  );
} 